import { useCallback, useRef, useState } from 'react';

import { useDocumentVisibilityDetection } from '@spotify/confidence-core-react';

import { isSignInRequiredError } from './useCreateAccount';

type UseEmailVerificationOptions = {
  getAccessTokenSilently: (opts: {
    cacheMode: 'on' | 'off' | 'cache-only';
  }) => Promise<string>;
  enabled: boolean;
};

export type EmailVerificationStatus =
  | 'idle'
  | 'checking'
  | 'pending'
  | 'error'
  | 'sign_in_required';

type UseEmailVerificationResult = {
  recheckEmailVerification: () => Promise<void>;
  status: EmailVerificationStatus;
};

export const useEmailVerification = ({
  getAccessTokenSilently,
  enabled,
}: UseEmailVerificationOptions): UseEmailVerificationResult => {
  const [status, setStatus] = useState<EmailVerificationStatus>('idle');
  const isCheckingRef = useRef(false);

  const recheckEmailVerification = useCallback(async (): Promise<void> => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    setStatus('checking');
    try {
      await getAccessTokenSilently({ cacheMode: 'off' });
      setStatus('pending');
    } catch (error) {
      setStatus(isSignInRequiredError(error) ? 'sign_in_required' : 'error');
    } finally {
      isCheckingRef.current = false;
    }
  }, [getAccessTokenSilently]);

  const handleVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (!isVisible || !enabled) return;
      void recheckEmailVerification();
    },
    [enabled, recheckEmailVerification],
  );
  useDocumentVisibilityDetection(handleVisibilityChange);

  return { recheckEmailVerification, status };
};
